import http, { Query, Method } from "./http";
import Cookies from "cookies";
import { GetServerSidePropsContext, PreviewData } from "next";
import { ParsedUrlQuery } from "querystring";
import getAuthHeaders from "./get-auth-headers";

export type { Query, Method };

const defaultParseResponse = <APIResponse>(
  response: Response
): Promise<APIResponse> =>
  response
    .text()
    .then((text) => {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    })
    .then((result) => {
      if (response.status < 400) return result;
      return Promise.reject(result);
    });

export type Props<APIResponse, Body> = {
  path: string;
  query?: Query;
  method?: Method;
  headers?: HeadersInit;
  body?: Body;
  parseResponse?: (
    response: Response,
    defaultParser: (response: Response) => Promise<APIResponse>
  ) => Promise<APIResponse> | APIResponse;
  appId?: string;
  context?: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>;
};

export const buildBasePath = (path: string) => {
  if (path.startsWith("http")) return path;

  const base = process.env.NEXT_PUBLIC_API_BASE_URL;
  const _path = path.startsWith("/") ? path : `/${path}`;
  return `${base}${_path}`;
};

function extractCookies(
  context?: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
) {
  if (context) {
    const { req, res } = context;
    const cookie = new Cookies(req, res);
    return cookie.request.headers.cookie || "";
  }

  if (typeof document === "undefined") return "";
  return document.cookie;
}

export default function api<
  Response = Record<string, unknown>,
  Body = Record<string, unknown>
>({
  method,
  path,
  query,
  body,
  headers,
  parseResponse,
  appId,
  context,
}: Props<Response, Body>): Promise<Response> {
  const cookie = extractCookies(context);

  const logStart = new Date();
  const logParams =
    query && new URLSearchParams(query as Record<string, string>).toString();
  const logReq = `${method ?? "GET"} ${path}${
    logParams ? `?${logParams}` : ""
  }`;
  // console.log(`${logStart.toISOString()} ${logReq}`);

  return http({
    url: buildBasePath(path),
    query,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(appId && getAuthHeaders(appId, cookie)),
      ...headers,
      ...(cookie && { cookie }),
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      console.log(
        `🕑 ${(Date.now() - logStart.getTime()) / 1e3}s ${
          response.status
        } ${logReq}`
      );
      if (parseResponse) return parseResponse(response, defaultParseResponse);
      return defaultParseResponse<Response>(response);
    })
    .catch((error) => {
      console.log(`🔴 ${(Date.now() - logStart.getTime()) / 1e3}s ${logReq}`);
      console.error(error);
      if (!context) return Promise.reject(error);

      const { res } = context;

      switch (error.detail) {
        case "Unauthorized":
          res.statusCode = 401;
          res.writeHead(302, {
            Location: "/401",
          });
          res.end();
          throw new UnauthorizedError(error.message);
        case "Forbidden":
          res.statusCode = 403;
          res.writeHead(302, {
            Location: "/403",
          });
          res.end();
          throw new ForbiddenError(error.message);
        default:
          return Promise.reject(error);
      }
    });
}

export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Unauthorized";
  }
}

export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Forbidden";
  }
}

export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotFound";
  }
}
